// src/components/auth/ProtectedRoute.tsx
import React from 'react';
import { Navigate } from '@tanstack/react-router';
import { Container, Paper, Stack, Loader, Text, Alert, Button } from '@mantine/core';
import { IconShield, IconLogin } from '@tabler/icons-react';
import { UserRole } from '../../types/user';
import { useOktaAuth } from '../../providers/okta-auth-provider';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  allowedRoles?: UserRole[];
  requireAnyRole?: boolean; // Se true, richiede solo di essere autenticati
  redirectTo?: string;
}

export function ProtectedRoute({
  children,
  requiredRole,
  allowedRoles,
  requireAnyRole = false,
  redirectTo = '/login',
}: ProtectedRouteProps) {
  const { isAuthenticated, userInfo, isLoading, hasRole } = useOktaAuth();

  // Mostra loader durante il caricamento
  if (isLoading) {
    return (
      <Container size="sm" py={100}>
        <Paper p="xl" shadow="md" radius="md">
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text>Verifica autenticazione...</Text>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // Redirect se non autenticato
  if (!isAuthenticated) {
    // Ricorda la pagina richiesta per tornarci dopo il login
    const { pathname, search } = window.location;
    if (!pathname.startsWith('/login')) {
      sessionStorage.setItem('originalUri', pathname + search);
    }
    return <Navigate to={redirectTo} />;
  }

  // Se richiede solo di essere autenticati (qualsiasi ruolo)
  if (requireAnyRole) {
    return <>{children}</>;
  }

  // Controlla ruolo specifico
  if (requiredRole && !hasRole([requiredRole])) {
    return <UnauthorizedAccess />;
  }

  // Controlla lista di ruoli permessi
  if (allowedRoles && !hasRole(allowedRoles)) {
    return <UnauthorizedAccess />;
  }

  // Se non ci sono restrizioni specifiche, verifica solo l'autenticazione
  return <>{children}</>;
}

function UnauthorizedAccess() {
  const { userInfo, logout } = useOktaAuth();

  const handleLogout = () => {
    logout();
  };

  return (
    <Container size="sm" py={100}>
      <Paper p="xl" shadow="md" radius="md">
        <Stack align="center" gap="md">
          <IconShield size={64} color="red" />
          
          <Stack gap="xs" align="center">
            <Text size="lg" fw={600}>Accesso Non Autorizzato</Text>
            <Text size="sm" c="dimmed" ta="center">
              Non hai i permessi necessari per accedere a questa sezione.
            </Text>
          </Stack>

          {userInfo && (
            <Alert variant="light" color="orange">
              <Text size="sm">
                <strong>Utente:</strong> {userInfo.displayName || userInfo.email}<br />
                <strong>Ruoli:</strong> {userInfo.roles.map(r => getRoleName(r)).join(', ')}
              </Text>
            </Alert>
          )}

          <Button
            leftSection={<IconLogin size={16} />}
            onClick={handleLogout}
            variant="outline"
          >
            Cambia Utente
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}

function getRoleName(role: UserRole): string {
  const roleNames: Record<UserRole, string> = {
    [UserRole.Admin]: 'Amministratore',
    [UserRole.AdminUfficioStile]: 'Capo Ufficio Stile',
    [UserRole.OperatoreUfficioStile]: 'Ufficio Stile',
    [UserRole.AdminSviluppo]: 'Capo Industrializzazione',
    [UserRole.OperatoreSviluppo]: 'Industrializzazione',
    [UserRole.User]: 'Utente',
  };
  
  return roleNames[role] || 'Ruolo Sconosciuto';
}

// Componenti helper per ruoli specifici
export function AdminOnlyRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute requiredRole={UserRole.Admin}>
      {children}
    </ProtectedRoute>
  );
}

export function CapoUfficioStileRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute allowedRoles={[UserRole.Admin, UserRole.AdminUfficioStile]}>
      {children}
    </ProtectedRoute>
  );
}

export function UfficioStileRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute 
      allowedRoles={[
        UserRole.Admin, 
        UserRole.AdminUfficioStile, 
        UserRole.OperatoreUfficioStile
      ]}
    >
      {children}
    </ProtectedRoute>
  );
}

export function CapoIndustrializzazioneRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute 
      allowedRoles={[
        UserRole.Admin, 
        UserRole.AdminSviluppo
      ]}
    >
      {children}
    </ProtectedRoute>
  );
}

export function IndustrializzazioneRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute 
      allowedRoles={[
        UserRole.Admin, 
        UserRole.AdminSviluppo, 
        UserRole.OperatoreSviluppo
      ]}
    >
      {children}
    </ProtectedRoute>
  );
}

export default ProtectedRoute;